import { supabase } from '../lib/supabaseClient';
import { listBlueprintsByReprocessRate } from './blueprintPerformanceService';
import { createWorkflowInstanceFromBlueprint } from '../modules/excelencia/services/excelenciaService';
import type {
  ProcessInstanceStatus,
  WorkflowPriority,
  TaskInstanceStatus,
} from '../types/workflow-v9';
import {
  getStagePerformance,
  getUsersPerformance,
  type StagePerformance,
  type UserPerformance,
} from './workflowService';
import {
  humanizeWorkflowRoleName,
  userHasWorkflowRole,
} from '../modules/shared/services/workflowRoleService';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || '';
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

export type ExecutionSortField = 'started_at' | 'updated_at' | 'priority' | 'status' | 'sla' | 'elapsed';
export type SortDirection = 'asc' | 'desc';

export interface ExecutionListFilters {
  status?: ProcessInstanceStatus[];
  priority?: WorkflowPriority[];
  templateId?: string;
  search?: string;
  overdue?: boolean;
}

export interface ExecutionListQuery {
  organizationId: string;
  page?: number;
  pageSize?: number;
  sortField?: ExecutionSortField;
  sortDirection?: SortDirection;
  filters?: ExecutionListFilters;
}

export interface ExecutionListItem {
  id: string;
  templateId: string;
  templateName?: string;
  templateVersionSemver?: string;
  referenceCode?: string;
  title?: string;
  status: ProcessInstanceStatus;
  priority?: WorkflowPriority;
  startedAt?: string;
  completedAt?: string;
  currentStageId?: string;
  currentStageName?: string;
  initiatedBy?: string;
  isOverdue: boolean;
  overdueMinutes?: number;
  elapsedMinutes?: number;
  totalDurationMinutes?: number;
  metadata?: Record<string, any> | null;
  contextData?: Record<string, any> | null;
  reprocessedFromId?: string | null;
  reprocessCount: number;
  firstTaskId?: string;
}

export interface ExecutionListResult {
  items: ExecutionListItem[];
  total: number;
  page: number;
  pageSize: number;
}

export interface ExecutionTemplateSummary {
  id: string;
  name: string;
  version: number;
  versionSemver?: string;
  category?: string;
  latestVersionId?: string;
}

export interface ExecutionTaskSummary {
  id: string;
  taskName?: string;
  stageName?: string;
  stageId?: string;
  status: TaskInstanceStatus;
  assignedRoleId?: string;
  assignedRoleKey?: string;
  assignedRoleName?: string;
  assignedUserId?: string;
  assignedUserName?: string;
  assignedTo?: string;
  assignedToName?: string;
  claimedAt?: string;
  startedAt?: string;
  completedAt?: string;
  slaDeadline?: string;
  isOverdue: boolean;
  notes?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export type ExecutionTaskSortField = 'created_at' | 'sla_deadline' | 'status';

export interface ExecutionTaskFilters {
  status?: TaskInstanceStatus[];
  stageName?: string;
  assigneeName?: string;
  overdue?: boolean;
  search?: string;
}

export interface ExecutionTaskListParams {
  organizationId: string;
  executionId: string;
  page?: number;
  pageSize?: number;
  sortField?: ExecutionTaskSortField;
  sortDirection?: SortDirection;
  filters?: ExecutionTaskFilters;
}

export interface ExecutionTaskListResult {
  items: ExecutionTaskSummary[];
  total: number;
  page: number;
  pageSize: number;
}

export interface ExecutionTimelineEntry {
  id: string;
  action: string;
  actionDescription?: string | null;
  justification?: string | null;
  performedByName?: string | null;
  performedByRole?: string;
  timestamp: string;
  stageName?: string | null;
  metadata?: Record<string, any> | null;
}

export interface ExecutionDetailsPayload {
  instance: ExecutionListItem;
  tasks: ExecutionTaskSummary[];
  timeline: ExecutionTimelineEntry[];
  lineage: ExecutionLineageInfo;
}

export interface ExecutionLineageNode {
  id: string;
  referenceCode?: string;
  status: ProcessInstanceStatus;
  startedAt?: string;
}

export interface ExecutionLineageInfo {
  origin: ExecutionLineageNode | null;
  children: ExecutionLineageNode[];
  reprocessCount: number;
}

export interface ExecutionAnalyticsParams {
  organizationId: string;
  templateId?: string;
  rangeInDays?: number;
}

export interface ExecutionKpis {
  totalExecutions: number;
  activeExecutions: number;
  overdueExecutions: number;
  completionRate: number;
  avgLeadTimeHours: number;
}

export interface SlaTrendPoint {
  date: string;
  onTime: number;
  late: number;
}

export interface ExecutionAnalyticsPayload {
  kpis: ExecutionKpis;
  slaTrend: SlaTrendPoint[];
  stagePerformance: StagePerformance[];
  userPerformance: UserPerformance[];
  reprocessRate: number;
  reprocessLeaderboard: ReprocessLeaderboardItem[];
  reprocessTrend: ReprocessTrendPoint[];
  blueprintQualityLeaders: BlueprintQualityLeader[];
}

export interface BlueprintQualityLeader {
  blueprintId: string;
  blueprintName: string | null;
  reprocessRate: number;
  totalExecutions: number;
}

export interface ReprocessLeaderboardItem {
  templateId: string;
  templateName?: string;
  totalReprocesses: number;
}

export interface ReprocessTrendPoint {
  date: string;
  reprocessCount: number;
}

export interface CreateExecutionInput {
  organizationId: string;
  templateId?: string;
  blueprintId?: string;
  versionId?: string;
  title: string;
  priority?: WorkflowPriority;
  initiatedBy?: string;
  contextData?: Record<string, any>;
}

export interface ExecutionMutationInput {
  organizationId: string;
  executionId: string;
  userId?: string;
  reason?: string;
}

export interface ReprocessExecutionInput {
  organizationId: string;
  executionId: string;
  reason?: string;
}

/**
 * Contrato padronizado para resposta de criação de execução.
 * Unifica retorno de createExecutionInstance() e createWorkflowInstanceFromBlueprint().
 */
export interface ExecutionInstanceResponse {
  instanceId: string;
  templateId: string;
  referenceCode?: string;
  title?: string;
  status: ProcessInstanceStatus;
  priority?: WorkflowPriority;
  tasksCreated?: number;
  firstTaskId?: string;
  sla?: { totalHours?: number } | null;
  startedAt?: string;
}

export interface ExecutionActionResponse<T = ExecutionListItem> {
  success: boolean;
  data?: T;
  error?: { code: string; message: string };
}

export async function listExecutionTemplates(organizationId: string): Promise<ExecutionTemplateSummary[]> {
  if (!organizationId) throw new Error('organizationId é obrigatório');

  // Blueprint = Governança. Execução busca do que está publicado.
  const { data, error } = await supabase
    .from('process_blueprints_with_latest_version')
    .select('id, name, version, version_semver, category, latest_version_id')
    .eq('organization_id', organizationId)
    .in('status', ['published', 'standard'])
    .order('name', { ascending: true });

  if (error) throw error;
  return (data ?? []).map((row: any) => ({
    id: row.id,
    name: row.name,
    version: row.version ?? 1,
    versionSemver: row.version_semver,
    category: row.category ?? undefined,
    latestVersionId: row.latest_version_id,
  }));
}

export async function listExecutionInstances(params: ExecutionListQuery): Promise<ExecutionListResult> {
  const {
    organizationId,
    page = 1,
    pageSize = 20,
    sortField = 'started_at',
    sortDirection = 'desc',
    filters = {},
  } = params;

  if (!organizationId) throw new Error('organizationId é obrigatório');

  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;

  let query = supabase
    .from('workflow_instances_v9')
    .select(
      `*,
       template:workflow_templates_v9(id, name, version, category),
       current_stage:workflow_stages_v9(id, name)`,
      { count: 'exact' }
    )
    .eq('organization_id', organizationId);

  if (filters.templateId) {
    query = query.eq('template_id', filters.templateId);
  }
  if (filters.status && filters.status.length > 0) {
    query = query.in('status', filters.status);
  }
  if (filters.priority && filters.priority.length > 0) {
    query = query.in('priority', filters.priority);
  }
  if (filters.overdue) {
    query = query.eq('is_overdue', true);
  }
  if (filters.search && filters.search.trim().length > 0) {
    const term = filters.search.trim();
    query = query.or(`reference_code.ilike.%${term}%,title.ilike.%${term}%`);
  }

  const orderColumn = sortField === 'sla'
    ? 'overdue_since'
    : sortField === 'elapsed'
      ? 'started_at'
      : sortField;
  query = query.order(orderColumn, {
    ascending: sortField === 'elapsed'
      ? sortDirection === 'desc'
      : sortDirection === 'asc',
  });

  const { data, error, count } = await query.range(from, to);
  if (error) throw error;

  const items = (data ?? []).map(normalizeExecutionRow);

  return {
    items,
    total: count ?? 0,
    page,
    pageSize,
  };
}

export async function getExecutionDetails(params: {
  organizationId: string;
  executionId: string;
}): Promise<ExecutionDetailsPayload> {
  const { organizationId, executionId } = params;
  if (!organizationId) throw new Error('organizationId é obrigatório');
  if (!executionId) throw new Error('executionId é obrigatório');

  const { data: instanceRow, error: instanceError } = await supabase
    .from('workflow_instances_v9')
    .select(
      `*,
       template:workflow_templates_v9(id, name, version, category),
       current_stage:workflow_stages_v9(id, name)`
    )
    .eq('organization_id', organizationId)
    .eq('id', executionId)
    .maybeSingle();

  if (instanceError) throw instanceError;
  if (!instanceRow) throw new Error('Execução não encontrada');

  const instance = normalizeExecutionRow(instanceRow);
  const tasksPromise = supabase
    .from('workflow_tasks_v9')
    .select(
      `*, stage:workflow_stages_v9(name, default_assignee_role)`
    )
    .eq('organization_id', organizationId)
    .eq('instance_id', executionId)
    .order('created_at', { ascending: true });

  const timelinePromise = supabase
    .from('workflow_task_logs')
    .select(
      `id,
       action,
       action_description,
       justification,
       performed_by_name,
       performed_by_role,
       timestamp,
       metadata,
       stage:workflow_stages_v9(name)`
    )
    .eq('organization_id', organizationId)
    .eq('process_instance_id', executionId)
    .order('timestamp', { ascending: false })
    .limit(100);

  const executionLogsPromise = supabase
    .from('execution_logs')
    .select('id, event_type, metadata, created_at')
    .eq('organization_id', organizationId)
    .eq('execution_id', executionId)
    .order('created_at', { ascending: false })
    .limit(25)
    .then((res) => res, () => ({ data: null, error: null } as { data: any; error: any }));

  const parentPromise = instance.reprocessedFromId
    ? supabase
        .from('workflow_instances_v9')
        .select('id, reference_code, status, started_at')
        .eq('organization_id', organizationId)
        .eq('id', instance.reprocessedFromId)
        .maybeSingle()
    : Promise.resolve({ data: null, error: null } as { data: any; error: any });

  const childrenPromise = supabase
    .from('workflow_instances_v9')
    .select('id, reference_code, status, started_at')
    .eq('organization_id', organizationId)
    .eq('reprocessed_from_id', executionId)
    .order('started_at', { ascending: false })
    .then((res) => res, () => ({ data: null, error: null } as { data: any; error: any }));

  const [
    { data: tasksData, error: tasksError },
    { data: timelineData, error: timelineError },
    { data: executionLogData },
    parentResult,
    { data: childrenData },
  ] = await Promise.all([tasksPromise, timelinePromise, executionLogsPromise, parentPromise, childrenPromise]);

  if (tasksError) throw tasksError;
  if (timelineError) throw timelineError;
  if (parentResult?.error) throw parentResult.error;

  const tasks = (tasksData ?? []).map(mapTaskRow);
  const timelineEntries: ExecutionTimelineEntry[] = (timelineData ?? []).map(mapTimelineRow);
  const executionTimelineEntries: ExecutionTimelineEntry[] = (executionLogData ?? []).map(mapExecutionLogRow);
  const timeline: ExecutionTimelineEntry[] = [...timelineEntries, ...executionTimelineEntries].sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
  );

  const origin = parentResult?.data
    ? {
        id: parentResult.data.id,
        referenceCode: parentResult.data.reference_code ?? undefined,
        status: parentResult.data.status as ProcessInstanceStatus,
        startedAt: parentResult.data.started_at ?? undefined,
      }
    : null;

  const children: ExecutionLineageNode[] = (childrenData ?? []).map((child: any) => ({
    id: child.id,
    referenceCode: child.reference_code ?? undefined,
    status: child.status as ProcessInstanceStatus,
    startedAt: child.started_at ?? undefined,
  }));

  const lineage: ExecutionLineageInfo = {
    origin,
    children,
    reprocessCount: instance.reprocessCount ?? 0,
  };

  return {
    instance,
    tasks,
    timeline,
    lineage,
  };
}

export async function listExecutionTasks(params: ExecutionTaskListParams): Promise<ExecutionTaskListResult> {
  const {
    organizationId,
    executionId,
    page = 1,
    pageSize = 25,
    sortField = 'created_at',
    sortDirection = 'desc',
    filters = {},
  } = params;

  if (!organizationId) throw new Error('organizationId é obrigatório');
  if (!executionId) throw new Error('executionId é obrigatório');

  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;

  let query = supabase
    .from('workflow_tasks_v9')
    .select(
      `*, stage:workflow_stages_v9(name, default_assignee_role)`,
      { count: 'exact' },
    )
    .eq('organization_id', organizationId)
    .eq('instance_id', executionId);

  if (filters.status && filters.status.length > 0) {
    query = query.in('status', filters.status);
  }
  if (filters.overdue) {
    query = query.eq('is_overdue', true);
  }
  if (filters.search && filters.search.trim().length > 0) {
    const term = filters.search.trim();
    query = query.ilike('notes', `%${term}%`);
  }

  const orderColumn = sortField === 'created_at' ? 'created_at' : sortField;
  query = query.order(orderColumn, {
    ascending: sortDirection === 'asc',
  });

  const { data, error, count } = await query.range(from, to);
  if (error) throw error;

  return {
    items: (data ?? []).map(mapTaskRow),
    total: count ?? 0,
    page,
    pageSize,
  };
}

export async function getExecutionAnalytics({
  organizationId,
  templateId,
  rangeInDays = 14,
}: ExecutionAnalyticsParams): Promise<ExecutionAnalyticsPayload> {
  if (!organizationId) throw new Error('organizationId é obrigatório');
  const since = new Date(Date.now() - rangeInDays * 24 * 60 * 60 * 1000).toISOString();

  let instanceQuery = supabase
    .from('workflow_instances_v9')
    .select(`*, template:workflow_templates_v9(id, name)`)
    .eq('organization_id', organizationId)
    .gte('started_at', since);

  if (templateId) {
    instanceQuery = instanceQuery.eq('template_id', templateId);
  }

  const { data: instances, error: instancesError } = await instanceQuery;
  if (instancesError) throw instancesError;

  const totalExecutions = instances?.length ?? 0;
  const activeExecutions = instances?.filter((i) => i.status === 'active').length ?? 0;
  const overdueExecutions = instances?.filter((i) => i.is_overdue).length ?? 0;
  const completedExecutions = instances?.filter((i) => i.status === 'completed').length ?? 0;

  // Calcular lead times em JS (total_duration_minutes pode nao existir como coluna)
  const now = Date.now();
  const leadTimes = (instances ?? [])
    .map((i: any) => {
      if (i.completed_at && i.started_at) {
        return Math.round((new Date(i.completed_at).getTime() - new Date(i.started_at).getTime()) / 60000);
      }
      if (i.status === 'active' && i.started_at) {
        return Math.round((now - new Date(i.started_at).getTime()) / 60000);
      }
      return null;
    })
    .filter(Boolean) as number[];
  const avgLeadTimeHours = leadTimes.length > 0
    ? Math.round(((leadTimes.reduce((sum, value) => sum + value, 0) / leadTimes.length) / 60) * 10) / 10
    : 0;

  const reprocessedInstances = (instances ?? []).filter((i) => i.reprocessed_from_id);
  const reprocessRate = totalExecutions > 0
    ? Math.round(((reprocessedInstances.length / totalExecutions) * 100) * 10) / 10
    : 0;

  const leaderboardMap = new Map<string, ReprocessLeaderboardItem>();
  reprocessedInstances.forEach((row: any) => {
    const templateId = row.template_id;
    if (!templateId) return;
    const current = leaderboardMap.get(templateId) ?? {
      templateId,
      templateName: row.template?.name ?? undefined,
      totalReprocesses: 0,
    };
    current.totalReprocesses += 1;
    if (!current.templateName && row.template?.name) {
      current.templateName = row.template.name;
    }
    leaderboardMap.set(templateId, current);
  });

  const reprocessLeaderboard = Array.from(leaderboardMap.values())
    .sort((a, b) => b.totalReprocesses - a.totalReprocesses)
    .slice(0, 5);

  const reprocessTrend = buildReprocessTrend(reprocessedInstances, rangeInDays);

  const kpis: ExecutionKpis = {
    totalExecutions,
    activeExecutions,
    overdueExecutions,
    completionRate: totalExecutions > 0 ? Math.round((completedExecutions / totalExecutions) * 1000) / 10 : 0,
    avgLeadTimeHours,
  };

  let tasksQuery = supabase
    .from('workflow_tasks_v9')
    .select('instance_id, completed_at, is_overdue')
    .eq('organization_id', organizationId)
    .not('completed_at', 'is', null)
    .gte('completed_at', since);

  let shouldFetchTasks = true;
  if (templateId) {
    const instanceIds = (instances ?? []).map((i) => i.id);
    if (instanceIds.length > 0) {
      tasksQuery = tasksQuery.in('instance_id', instanceIds);
    } else {
      shouldFetchTasks = false;
    }
  }

  let tasksData: any[] = [];
  if (shouldFetchTasks) {
    const { data, error: tasksError } = await tasksQuery;
    if (tasksError) throw tasksError;
    tasksData = data ?? [];
  }

  const slaTrend = buildSlaTrend(tasksData, rangeInDays);

  const [stagePerformance, userPerformance, blueprintQualityRaw] = await Promise.all([
    getStagePerformance(organizationId),
    getUsersPerformance(organizationId, 5),
    listBlueprintsByReprocessRate({ organizationId, limit: 5 }),
  ]);

  const blueprintQualityLeaders: BlueprintQualityLeader[] = blueprintQualityRaw.map((item) => ({
    blueprintId: item.blueprintId,
    blueprintName: item.blueprintName ?? null,
    reprocessRate: item.reprocessRate,
    totalExecutions: item.totalExecutions,
  }));

  return {
    kpis,
    slaTrend,
    stagePerformance,
    userPerformance,
    reprocessRate,
    reprocessLeaderboard,
    reprocessTrend,
    blueprintQualityLeaders,
  };
}

export async function createExecutionInstance(input: CreateExecutionInput): Promise<ExecutionListItem> {
  const {
    organizationId,
    templateId,
    blueprintId,
    versionId,
    title,
    priority = 'media',
    initiatedBy,
    contextData = {},
  } = input;

  const auditEnabled = import.meta.env.DEV;
  if (auditEnabled) {
    console.groupCollapsed('[AUDIT] Execution Create');
    console.log('payload', { organizationId, blueprintId, versionId, templateId, title });
    console.groupEnd();
  }

  if (!organizationId) throw new Error('organizationId é obrigatório');
  if (!title) throw new Error('title é obrigatório');

  // PRIORIDADE 1: Se temos blueprintId, usar o caminho de Excelência
  if (blueprintId) {
    const result = await createWorkflowInstanceFromBlueprint({
      blueprintId,
      versionId,
      organizationId,
      title,
      priority,
      initiatedBy,
      contextData,
    });
    
    // Retornar no formato ExecutionListItem usando BlueprintInstanceResult
    return {
      id: result.instanceId,
      templateId: result.templateId,
      templateName: undefined,
      referenceCode: result.referenceCode,
      title,
      status: 'active' as ProcessInstanceStatus,
      priority: priority as WorkflowPriority,
      startedAt: new Date().toISOString(),
      currentStageId: undefined,
      isOverdue: false,
      reprocessCount: 0,
      firstTaskId: result.firstTaskId,
    };
  }

  // PRIORIDADE 2: Legado / Direto via TemplateId
  if (!templateId) throw new Error('Selecione um template ou blueprint');

  const firstStage = await getFirstStage(templateId);
  if (!firstStage?.id) {
    throw new Error('Template selecionado não possui etapas configuradas');
  }

  if (initiatedBy && firstStage.default_assignee_role) {
    const canInitiate = await userHasWorkflowRole(
      initiatedBy,
      organizationId,
      null,
      firstStage.default_assignee_role ?? null,
    );
    if (!canInitiate) {
      throw new Error('O usuario que inicia a execucao nao pertence ao papel exigido pela primeira etapa.');
    }
  }

  const referenceCode = await generateReferenceCode(organizationId);

  const instancePayload = {
    template_id: templateId,
    organization_id: organizationId,
    reference_code: referenceCode,
    title,
    status: 'active',
    priority,
    current_stage_id: firstStage.id,
    initiated_by: initiatedBy,
    started_at: new Date().toISOString(),
    context_data: contextData,
    metadata: { 
      priority,
      blueprint_id: blueprintId,
      blueprint_version_id: versionId
    },
    // Rastreabilidade direta nas colunas novas
    blueprint_id: blueprintId,
    blueprint_version_id: versionId
  };
  if (auditEnabled) {
    console.groupCollapsed('[AUDIT] Execution Create Payload');
    console.log('payload', instancePayload);
    console.groupEnd();
  }

  const { data: instanceRow, error, status } = await supabase
    .from('workflow_instances_v9')
    .insert(instancePayload)
    .select(`*, template:workflow_templates_v9(id, name, version), current_stage:workflow_stages_v9(id, name)`)
    .single();

  if (auditEnabled) {
    console.groupCollapsed('[AUDIT] Execution Create Response');
    console.log('response', { data: instanceRow, error, status });
    console.groupEnd();
  }

  if (error) throw error;

  const firstTaskId = await createFirstTaskForInstance(instanceRow.id, firstStage.id, organizationId);

  return { ...normalizeExecutionRow(instanceRow), firstTaskId };
}

export async function cancelExecutionInstance({
  organizationId,
  executionId,
  reason,
  userId,
}: ExecutionMutationInput): Promise<void> {
  if (!organizationId) throw new Error('organizationId é obrigatório');
  if (!executionId) throw new Error('executionId é obrigatório');
  await invokeExecutionAction(executionId, 'cancel', { reason });
}

export async function pauseExecutionInstance({
  organizationId,
  executionId,
}: ExecutionMutationInput): Promise<void> {
  if (!organizationId) throw new Error('organizationId é obrigatório');
  if (!executionId) throw new Error('executionId é obrigatório');
  await invokeExecutionAction(executionId, 'pause');
}

export async function resumeExecutionInstance({
  organizationId,
  executionId,
}: ExecutionMutationInput): Promise<void> {
  if (!organizationId) throw new Error('organizationId é obrigatório');
  if (!executionId) throw new Error('executionId é obrigatório');
  await invokeExecutionAction(executionId, 'resume');
}

export async function reprocessExecutionInstance({
  organizationId,
  executionId,
  reason,
}: ReprocessExecutionInput): Promise<ExecutionListItem> {
  if (!organizationId) throw new Error('organizationId é obrigatório');
  if (!executionId) throw new Error('executionId é obrigatório');
  const summary = await invokeExecutionAction<ExecutionListItem>(executionId, 'reprocess', { reason });
  if (!summary) {
    throw new Error('Resposta inválida ao reprocessar execução');
  }
  return summary;
}

async function invokeExecutionAction<T = ExecutionListItem>(executionId: string, action: string, payload?: Record<string, any>) {
  if (!SUPABASE_URL) throw new Error('SUPABASE_URL não configurada');
  if (!SUPABASE_ANON_KEY) throw new Error('SUPABASE_ANON_KEY não configurada');

  const { data: sessionData } = await supabase.auth.getSession();
  const accessToken = sessionData?.session?.access_token;
  if (!accessToken) throw new Error('Usuário não autenticado');

  const response = await fetch(`${SUPABASE_URL}/functions/v1/api/executions/${executionId}/${action}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      apikey: SUPABASE_ANON_KEY,
    },
    body: JSON.stringify(payload ?? {}),
  });

  const result = (await response.json()) as ExecutionActionResponse<T>;
  if (!response.ok || !result.success) {
    throw new Error(result.error?.message ?? 'Falha ao executar ação na instância');
  }
  return result.data as T;
}

function normalizeExecutionRow(row: any): ExecutionListItem {
  const stage = row.current_stage;
  const template = row.template;
  const startedAt = row.started_at ? String(row.started_at) : undefined;
  const completedAt = row.completed_at ? String(row.completed_at) : undefined;
  const overdueSince = row.overdue_since ? new Date(row.overdue_since).getTime() : null;
  const overdueMinutes = overdueSince ? Math.max(Math.round((Date.now() - overdueSince) / 60000), 1) : undefined;
  const elapsedMinutes = startedAt ? Math.max(Math.round((Date.now() - new Date(startedAt).getTime()) / 60000), 1) : undefined;

  return {
    id: row.id,
    templateId: row.template_id,
    templateName: template?.name ?? undefined,
    templateVersionSemver:
      template?.version_semver ??
      (template?.version !== null && template?.version !== undefined ? String(template.version) : undefined),
    referenceCode: row.reference_code ?? undefined,
    title: row.title ?? template?.name ?? undefined,
    status: row.status as ProcessInstanceStatus,
    priority: row.priority as WorkflowPriority | undefined,
    startedAt,
    completedAt,
    currentStageId: row.current_stage_id ?? undefined,
    currentStageName: stage?.name ?? undefined,
    initiatedBy: row.initiated_by ?? undefined,
    isOverdue: Boolean(row.is_overdue),
    overdueMinutes,
    elapsedMinutes,
    totalDurationMinutes: row.total_duration_minutes ?? undefined,
    metadata: row.metadata ?? null,
    contextData: row.context_data ?? null,
    reprocessedFromId: row.reprocessed_from_id ?? undefined,
    reprocessCount: typeof row.reprocess_count === 'number' ? row.reprocess_count : 0,
  };
}

function mapTaskRow(row: any): ExecutionTaskSummary {
  return {
    id: row.id,
    taskName: row.task_name ?? row.stage?.name ?? undefined,
    stageName: row.stage_name ?? row.stage?.name ?? undefined,
    stageId: row.stage_id ?? undefined,
    status: row.status as TaskInstanceStatus,
    assignedRoleId: row.metadata?.roleId ?? undefined,
    assignedRoleKey: row.metadata?.role ?? undefined,
    assignedRoleName: row.metadata?.roleName ?? row.stage?.default_assignee_role ?? undefined,
    assignedUserId: row.assigned_to ?? undefined,
    assignedUserName: row.assigned_to_name ?? undefined,
    assignedTo: row.assigned_to ?? undefined,
    assignedToName: row.assigned_to_name ?? undefined,
    claimedAt: row.assigned_at ?? undefined,
    startedAt: row.started_at ?? undefined,
    completedAt: row.completed_at ?? undefined,
    slaDeadline: row.sla_deadline ?? undefined,
    isOverdue: Boolean(row.is_overdue),
    notes: row.notes ?? null,
    createdAt: row.created_at ?? undefined,
    updatedAt: row.updated_at ?? undefined,
  };
}

function mapTimelineRow(row: any): ExecutionTimelineEntry {
  return {
    id: row.id,
    action: row.action,
    actionDescription: row.action_description ?? null,
    justification: row.justification ?? null,
    performedByName: row.performed_by_name ?? null,
    performedByRole: row.performed_by_role ?? undefined,
    timestamp: row.timestamp,
    stageName: row.stage?.name ?? null,
    metadata: row.metadata ?? null,
  };
}

function mapExecutionLogRow(row: any): ExecutionTimelineEntry {
  return {
    id: row.id,
    action: row.event_type,
    actionDescription: row.event_type === 'EXECUTION_REPROCESSED' ? 'Execução reprocessada' : row.event_type,
    justification: row.metadata?.reason ?? null,
    performedByName: row.metadata?.actor_name ?? null,
    performedByRole: undefined,
    timestamp: row.created_at,
    stageName: null,
    metadata: row.metadata ?? null,
  };
}

function buildSlaTrend(rows: any[], rangeInDays: number): SlaTrendPoint[] {
  const points: Record<string, SlaTrendPoint> = {};
  const now = new Date();

  for (let i = rangeInDays - 1; i >= 0; i -= 1) {
    const date = new Date(now.getTime() - i * 24 * 60 * 60 * 1000);
    const key = date.toISOString().slice(0, 10);
    points[key] = { date: key, onTime: 0, late: 0 };
  }

  rows.forEach((row) => {
    if (!row.completed_at) return;
    const key = String(row.completed_at).slice(0, 10);
    if (!points[key]) return;
    if (row.is_overdue) {
      points[key].late += 1;
    } else {
      points[key].onTime += 1;
    }
  });

  return Object.values(points);
}

function buildReprocessTrend(rows: any[], rangeInDays: number): ReprocessTrendPoint[] {
  const points: Record<string, ReprocessTrendPoint> = {};
  const now = new Date();

  for (let i = rangeInDays - 1; i >= 0; i -= 1) {
    const date = new Date(now.getTime() - i * 24 * 60 * 60 * 1000);
    const key = date.toISOString().slice(0, 10);
    points[key] = { date: key, reprocessCount: 0 };
  }

  rows.forEach((row) => {
    if (!row.started_at) return;
    const key = String(row.started_at).slice(0, 10);
    if (!points[key]) return;
    points[key].reprocessCount += 1;
  });

  return Object.values(points);
}

async function getFirstStage(templateId: string): Promise<{
  id: string;
  default_assignee_role?: string | null;
} | null> {
  const { data, error } = await supabase
    .from('workflow_stages_v9')
    .select('id, default_assignee_role')
    .eq('template_id', templateId)
    .order('stage_order', { ascending: true })
    .limit(1)
    .maybeSingle();

  if (error) throw error;
  return data ?? null;
}

async function generateReferenceCode(organizationId: string): Promise<string> {
  const year = new Date().getFullYear();
  const prefix = 'OS';

  const { data } = await supabase
    .from('workflow_instances_v9')
    .select('reference_code')
    .eq('organization_id', organizationId)
    .like('reference_code', `${prefix}-${year}-%`)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  let nextNumber = 1;
  if (data?.reference_code) {
    const match = data.reference_code.match(/(\d+)$/);
    if (match) {
      nextNumber = parseInt(match[1], 10) + 1;
    }
  }

  return `${prefix}-${year}-${String(nextNumber).padStart(3, '0')}`;
}

async function createFirstTaskForInstance(
  instanceId: string,
  stageId: string,
  organizationId: string,
): Promise<string | undefined> {
  const { data: stage } = await supabase
    .from('workflow_stages_v9')
    .select('name, sla_hours, warning_threshold_percent, default_assignee_role, responsible_role_id, department_id, metadata')
    .eq('id', stageId)
    .maybeSingle();

  const now = new Date();
  const slaDeadline = stage?.sla_hours
    ? new Date(now.getTime() + stage.sla_hours * 60 * 60 * 1000)
    : null;

  const warningThreshold = stage?.warning_threshold_percent ?? 80;
  const slaWarningAt = stage?.sla_hours
    ? new Date(now.getTime() + (stage.sla_hours * 60 * 60 * 1000 * warningThreshold) / 100)
    : null;

  const payload: Record<string, any> = {
    instance_id: instanceId,
    stage_id: stageId,
    organization_id: organizationId,
    status: 'pending',
    assigned_to: null,
    assigned_at: null,
    assigned_role_id: stage?.responsible_role_id ?? stage?.metadata?.roleId ?? null,
    assigned_department_id: stage?.department_id ?? stage?.metadata?.department_id ?? null,
    stage_name: stage?.name ?? null,
    is_overdue: false,
    metadata: {
      ...(stage?.metadata ?? {}),
      role: stage?.metadata?.role ?? stage?.default_assignee_role ?? null,
      roleId: stage?.metadata?.roleId ?? null,
      roleName: stage?.metadata?.roleName ?? humanizeWorkflowRoleName(stage?.default_assignee_role ?? null) ?? null,
      role_snapshot: {
        role_id: stage?.responsible_role_id ?? stage?.metadata?.roleId ?? null,
        role_name: stage?.metadata?.roleName ?? stage?.default_assignee_role ?? null,
      },
      department_snapshot: {
        department_id: stage?.department_id ?? stage?.metadata?.department_id ?? null,
        department_name: stage?.metadata?.department_name ?? null,
      },
      operationalMetrics: {
        ...((stage?.metadata?.operationalMetrics as Record<string, unknown> | undefined) ?? {}),
        enteredAt: now.toISOString(),
        updatedAt: now.toISOString(),
      },
      operationalHistory: [
        ...(((stage?.metadata?.operationalHistory as unknown[]) ?? []) as unknown[]),
        {
          event: 'activated',
          capturedAt: now.toISOString(),
          enteredAt: now.toISOString(),
          updatedAt: now.toISOString(),
          result: null,
        },
      ],
    },
    created_at: now.toISOString(),
    updated_at: now.toISOString(),
  };

  if (slaDeadline) {
    payload.sla_deadline = slaDeadline.toISOString();
  }
  const { data, error } = await supabase.from('workflow_tasks_v9').insert(payload).select('id').single();
  if (error) throw error;
  return data?.id;
}
